import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useRefreshGuard } from '../refreshGuards'
import { RotateCcw } from 'lucide-react'
import { useTasks } from '../TaskProvider'
import { roomRequest } from '../roomApi'
import { useRouteState } from '../routeState'
import { WorkspaceDialog } from './WorkspaceDialog'
import type { ProjectSummary } from '../types'

export function TrashProjects({ host }: { host: Element | null }) {
  const { user, members } = useTasks()
  const [open, setOpen] = useRouteState<boolean>('trash', false)
  const [selected, setSelected] = useRouteState<string | null>('approve', null)
  const [projects, setProjects] = useState<ProjectSummary[]>([])
  const [title, setTitle] = useState(''), [error, setError] = useState(''), [busy, setBusy] = useState(false)
  useRefreshGuard(busy || (!!selected && !!title), 'Finish or cancel your Trash action before refreshing.')
  const refresh = useCallback(async () => {
    try { setProjects((await roomRequest<{ projects: ProjectSummary[] }>(user.id, '/api/trash')).projects) }
    catch (e) { setError(e instanceof Error ? e.message : 'Trash could not be loaded.') }
  }, [user.id])
  useEffect(() => { void refresh(); const fn = () => void refresh(); window.addEventListener('bramble:remote-change', fn); return () => window.removeEventListener('bramble:remote-change', fn) }, [refresh])
  useEffect(() => { setTitle(''); setError('') }, [selected])
  const act = async (project: ProjectSummary, action: string) => {
    setBusy(true); setError('')
    try {
      await roomRequest(user.id, `/api/trash/${encodeURIComponent(project.id)}/${action}`, 'POST', { token: project.trash!.token, title })
      setSelected(null); await refresh()
      window.dispatchEvent(new Event('bramble:remote-change'))
    } catch (e) { setError(e instanceof Error ? e.message : 'This change could not be saved.') }
    finally { setBusy(false) }
  }
  const waiting = projects.filter(p => p.trash!.approvals.length && !p.trash!.approvals.includes(user.id)).length
  return <>
    {host && createPortal(<button type="button" className="library-trash-link" aria-label={`Project Trash${waiting ? ` · ${waiting} awaiting your approval` : ''}`} onClick={() => { setOpen(true); void refresh() }}>Project Trash{waiting > 0 && <b>{waiting}</b>}</button>, host)}
    {open && <WorkspaceDialog className="trash-dialog" title="Project Trash" eyebrow="RECOVER FIRST. DELETE TOGETHER." onClose={() => { if (!busy) { setOpen(false); setSelected(null) } }}>
      <div className="trash-content">
        <p>Either writer can restore a project. Permanent deletion needs approval from both silacel and spunkySarb. Nothing expires automatically.</p>
        {error && <p role="alert">{error}</p>}
        {!projects.length && <p>Trash is empty.</p>}
        {projects.map(project => <section key={project.id} className="trash-project">
          <h3>{project.title}</h3>
          <p>Moved here by {project.trash!.movedBy.name} · {new Date(project.trash!.movedAt).toLocaleDateString()}</p>
          <p>{project.trash!.approvals.length ? 'Permanent deletion approved by ' + project.trash!.approvals.map(id => members.find(m => m.id === id)?.displayName || 'a writer').join(', ') + '. Waiting for the other writer.' : 'Story, Bible and saved drafts are recoverable.'}</p>
          <div className="trash-actions">
            <button className="button" disabled={busy} onClick={() => void act(project, 'restore')}><RotateCcw size={14} />Restore</button>
            {project.trash!.approvals.length > 0 && <button className="button" disabled={busy} onClick={() => void act(project, 'cancel')}>Cancel deletion</button>}
            {!project.trash!.approvals.includes(user.id) && <button className="text-button" disabled={busy} onClick={() => setSelected(project.id)}>Approve permanent deletion…</button>}
          </div>
          {selected === project.id && <form className="trash-confirmation" onSubmit={e => { e.preventDefault(); void act(project, 'approve') }}>
            <p>{project.trash!.approvals.length ? 'Your approval will permanently delete this story and its saved drafts.' : 'Your approval will ask the other writer to confirm permanent deletion.'}</p>
            <label>Type the project title to approve<input autoFocus value={title} onChange={e => setTitle(e.target.value)} autoComplete="off" /></label>
            <button className="button button--danger" disabled={busy || title !== project.title}>{project.trash!.approvals.length ? 'Approve and permanently delete' : 'Approve deletion'}</button>
            <button type="button" className="text-button" disabled={busy} onClick={() => setSelected(null)}>Keep it in Trash</button>
          </form>}
        </section>)}
      </div>
    </WorkspaceDialog>}
  </>
}
